/**
 * Item render stills for the meta shell's cards — "renders, not rows"
 * (design/gdd/meta-visual-language.md §5).
 *
 * The PNGs are pre-rendered, transparent-background stills served as static assets
 * (`tools/ui-stills/render-stills.mjs` writes them; re-run it when the catalogue or a model
 * changes) — deliberately NOT live Three.js canvases: these cards render in the main chunk,
 * which must stay free of the renderer, and a grid that spins up a WebGL context per tile is a
 * phone-killer. The armoury/loadout keep their live previews; a card gets a picture.
 *
 * A missing still must never break a card: `stillImg` swaps in the caller's fallback node on
 * error, so a skin id with no rendered file (a brand-new catalogue entry before the pipeline
 * re-ran) degrades to the colour swatch the cards showed before stills existed.
 */

export function skinStillPath(skinId: number): string {
  return `/game/ui/skins/${skinId}.png`;
}

/**
 * The still for a weapon. ONE per weapon, in stock finish, and deliberately not one per finish.
 *
 * It used to take a skin id and resolve `<id>-s<skinId>.png`: 160 committed PNGs, and over 300
 * once the catalogue reached fifteen variants per gun (2026-09-10) — some 20MB of art in git
 * for pictures generated from four numbers, every one of which had to be re-rendered before a
 * new finish could be shown. A finish is now drawn from the same painter that paints the gun
 * (`applyFinishSwatch`, arena/weapon-finish.ts), so the card shows the weapon and the swatch
 * shows the finish, both from one source of truth and neither from a file.
 *
 * The `<weaponId>-s<skinId>.png` files are orphaned by that change and can be deleted.
 */
export function weaponStillPath(weaponId: number): string {
  return `/game/ui/weapons/${weaponId}.png`;
}

export interface StillImgOptions {
  src: string;
  alt: string;
  /** Intrinsic pixel size of the PNG — set as attributes so the layout never shifts while it
   * streams in. Display size is the caller's CSS classes. */
  width: number;
  height: number;
  className?: string;
  /** Swapped in place of the image if the file 404s or fails to decode. */
  fallback?: () => Node;
}

export function stillImg(opts: StillImgOptions): HTMLImageElement {
  const img = document.createElement('img');
  img.src = opts.src;
  img.alt = opts.alt;
  img.width = opts.width;
  img.height = opts.height;
  img.loading = 'lazy';
  img.decoding = 'async';
  if (opts.className) img.className = opts.className;
  if (opts.fallback) {
    const fallback = opts.fallback;
    img.addEventListener('error', () => img.replaceWith(fallback()), { once: true });
  }
  return img;
}

/** `skin:9` → 9; anything else (other kinds, non-numeric ids) → null. */
export function skinIdFromItemId(itemId: string): number | null {
  const match = /^skin:(\d+)$/.exec(itemId);
  return match ? Number(match[1]) : null;
}
